import {Box, Skeleton, Theme, Typography, useTheme} from '@mui/material';
import {SxProps} from '@mui/system';

type PillVariant =
  | 'success'
  | 'warning'
  | 'error'
  | 'info'
  | 'feature'
  | 'suggestion';

type VariantStyles = {
  bg: string;
  text: string;
  fontSize?: string;
  boxSx?: SxProps;
};

const resolveVariant = (
  theme: Theme,
  variant: PillVariant,
  size: 'sm' | 'md',
): VariantStyles => {
  switch (variant) {
    case 'success':
      return {bg: theme.palette.success.main, text: 'white'};
    case 'warning':
      return {bg: theme.palette.warning.main, text: 'white'};
    case 'error':
      return {bg: theme.palette.error.main, text: 'white'};
    case 'info':
      return {bg: theme.palette.info.main, text: 'white'};
    case 'feature':
      return {
        bg: `${theme.palette.info.main}20`,
        text: theme.palette.info.dark,
        fontSize: '10px',
        boxSx: {px: 0.5, py: 0},
      };
    case 'suggestion': {
      const fontSize =
        size === 'md' ? '14px' : (theme.tokens.text.badge.fontSize ?? '12px');
      return {
        bg: theme.tokens.colors.badge.default.bg,
        text: theme.palette.text.secondary,
        fontSize,
        boxSx: {
          padding: theme.spacing(0.25, 0.75),
          borderRadius: theme.tokens.radius.badge ?? 4,
        },
      };
    }
  }
};

export type PillProps = {
  color?: string;
  textColor?: string;
  label?: string;
  sx?: SxProps;
  startIcon?: React.ReactNode;
  loading?: boolean;
  skeletonWidth?: number;
  variant?: PillVariant;
  size?: 'sm' | 'md';
  maxWidth?: number | string;
};

export const Pill = ({
  label,
  color,
  sx,
  textColor,
  startIcon,
  loading,
  skeletonWidth,
  variant,
  size = 'sm',
  maxWidth,
}: PillProps) => {
  const theme = useTheme();

  const variantStyles = variant ? resolveVariant(theme, variant, size) : null;
  const resolvedBg = color ?? variantStyles?.bg;
  const resolvedText = textColor ?? variantStyles?.text ?? 'white';
  const resolvedFontSize =
    variantStyles?.fontSize ?? (size === 'md' ? '14px' : '12px');

  if (loading || !label || (!color && !variant)) {
    return (
      <Skeleton
        variant="rectangular"
        height={18}
        width={skeletonWidth ?? 100}
        sx={{
          borderRadius: '4px',
          my: '3px',
        }}
      />
    );
  }

  return (
    <Box
      bgcolor={resolvedBg}
      sx={{
        color: resolvedText,
        padding: '0px 4px',
        borderRadius: '3px',
        width: 'fit-content',
        height: 'fit-content',
        display: 'flex',
        alignItems: 'center',
        gap: variant === 'suggestion' ? 0.5 : 1,
        ...(maxWidth ? {maxWidth} : {}),
        ...(variantStyles?.boxSx as object),
        ...(sx as object),
      }}
    >
      {startIcon}
      <Typography
        fontWeight="500"
        color={resolvedText}
        noWrap
        fontSize={resolvedFontSize}
        sx={
          maxWidth
            ? {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }
            : undefined
        }
      >
        {label}
      </Typography>
    </Box>
  );
};
